import { z } from "zod";
import { IsoDateTime, Uuid, pageOf, withoutLink } from "./common";
import { BillingMode, Country, Language, Role, TenantDto, TenantStatus, Timezone, UiLang, Vertical, WhiteLabel } from "./identity";
import { LiveSwitchState } from "./platform";
import { SsoExchangeResponse } from "./sso";

export * from "./pricing";

export const Period = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "period_invalid");
export type Period = z.infer<typeof Period>;

/** What Stripe says about the subscription, plus `none` before the first checkout. */
export const SUBSCRIPTION_STATUSES = ["none", "incomplete", "trialing", "active", "past_due", "unpaid", "canceled"] as const;
export const SubscriptionStatus = z.enum(SUBSCRIPTION_STATUSES);
export type SubscriptionStatus = z.infer<typeof SubscriptionStatus>;

export const USAGE_KINDS = ["ai", "sms"] as const;
export const UsageKind = z.enum(USAGE_KINDS);
export type UsageKind = z.infer<typeof UsageKind>;

export const USAGE_LINE_STATUSES = ["pending", "sending", "reported", "nothing_to_bill", "waiting_customer", "skipped", "failed", "needs_review"] as const;
export const UsageLineStatus = z.enum(USAGE_LINE_STATUSES);
export type UsageLineStatus = z.infer<typeof UsageLineStatus>;

export const UsageLineDto = z.object({
  period: Period,
  kind: UsageKind,
  quantity: z.number().int().min(0),
  amountMinor: z.number().int().min(0),
  currency: z.string(),
  status: UsageLineStatus,
  reportedAt: IsoDateTime.nullable(),
});
export type UsageLineDto = z.infer<typeof UsageLineDto>;

export const LocationTierDto = z.object({ upTo: z.number().int().nullable(), monthlyMinor: z.number().int() });

/** What a tenant sees of its own billing. Who set a free account is the platform's record, not shown here. */
export const BillingDto = z.object({
  mode: BillingMode,
  /** Set in `host` mode: who sends the invoice. */
  billedBy: z.string().nullable(),
  status: SubscriptionStatus,
  locations: z.number().int().min(1),
  monthlyTotalMinor: z.number().int().nullable(),
  currency: z.string(),
  tiers: z.array(LocationTierDto),
  smsUnitMinor: z.number().int(),
  currentPeriodEnd: IsoDateTime.nullable(),
  cancelAtPeriodEnd: z.boolean(),
  /** When the first unpaid invoice failed. */
  paymentFailedAt: IsoDateTime.nullable(),
  /** Until when nothing changes for this tenant. */
  graceUntil: IsoDateTime.nullable(),
  /** Whether new campaigns, generation and ads may start. What already runs is never stopped by this. */
  newWorkAllowed: z.boolean(),
  /** Why not: `subscription_required`, `payment_overdue` or `subscription_canceled`. */
  newWorkBlockedBy: z.string().nullable(),
  /** The provider is configured, so the buttons can do something. */
  providerReady: z.boolean(),
  usage: z.array(UsageLineDto),
});
export type BillingDto = z.infer<typeof BillingDto>;

/** What every member may know, because it explains why a button says no. Amounts stay with the owner. */
export const BillingStatusDto = BillingDto.pick({ mode: true, status: true, paymentFailedAt: true, graceUntil: true, newWorkAllowed: true, newWorkBlockedBy: true });
export type BillingStatusDto = z.infer<typeof BillingStatusDto>;

export const CheckoutRequest = z.strictObject({ locations: z.number().int().min(1).max(500) });
export type CheckoutRequest = z.infer<typeof CheckoutRequest>;

export const RedirectDto = z.object({ url: z.url() });
export type RedirectDto = z.infer<typeof RedirectDto>;

// ---------------------------------------------------------------------------
// Direct accounts
// ---------------------------------------------------------------------------

export const ACCOUNT_KINDS = ["business", "agency"] as const;
const Email = z.string().trim().min(3).max(254).email();

/** The version of the terms and privacy pages a signup accepts. One place; a later version asks nobody again until that is built (docs/questions/s9-billing.md). */
export const TERMS_VERSION = "2026-11-03";

export const SignupRequest = z.strictObject({
  email: Email,
  name: withoutLink(z.string().trim().min(1).max(120)),
  company: withoutLink(z.string().trim().min(1).max(200)),
  kind: z.enum(ACCOUNT_KINDS).default("business"),
  country: Country,
  vertical: Vertical,
  languages: z.array(Language).min(1).max(8).optional(),
  timezone: Timezone.optional(),
  lang: UiLang.default("en"),
  /** Optional in the shape only, so a missing tick is refused with `terms_required` and not as a malformed body. */
  termsVersion: z.string().trim().max(32).optional(),
});
export type SignupRequest = z.input<typeof SignupRequest>;

export const LoginLinkRequest = z.strictObject({ email: Email, lang: UiLang.default("en") });
export type LoginLinkRequest = z.input<typeof LoginLinkRequest>;

/** The same answer whether or not the address has an account, so the answer says nothing about the address. */
export const AccountAccepted = z.object({ ok: z.literal(true) });

export const VerifyLinkRequest = z.strictObject({ token: z.string().min(16).max(256) });

export const DirectSession = SsoExchangeResponse;

export const AgencyTenantRequest = z.strictObject({
  name: withoutLink(z.string().trim().min(1).max(200)),
  country: Country,
  vertical: Vertical,
  languages: z.array(Language).min(1).max(8).optional(),
  timezone: Timezone.optional(),
});
export type AgencyTenantRequest = z.input<typeof AgencyTenantRequest>;

/** How the pages of a host look before anyone signed in. */
export const PublicBrandDto = z.object({
  name: z.string(),
  logoUrl: z.string().nullable(),
  theme: WhiteLabel.shape.theme.nullable(),
  whiteLabeled: z.boolean(),
  /** Whether people sign up and sign in here, or arrive from the host's own login. */
  directAccounts: z.boolean(),
  supportEmail: z.string().nullable(),
});
export type PublicBrandDto = z.infer<typeof PublicBrandDto>;

// ---------------------------------------------------------------------------
// Platform admin
// ---------------------------------------------------------------------------

export const PLATFORM_ROLES = ["operator", "viewer"] as const;
export const PlatformRole = z.enum(PLATFORM_ROLES);
export type PlatformRole = z.infer<typeof PlatformRole>;

export const PlatformOperatorDto = z.object({ id: Uuid, email: z.string(), name: z.string(), role: PlatformRole });
export type PlatformOperatorDto = z.infer<typeof PlatformOperatorDto>;

export const PlatformSession = z.object({ token: z.string(), expiresAt: IsoDateTime, operator: PlatformOperatorDto });
export type PlatformSession = z.infer<typeof PlatformSession>;

/** Where the e-mail link leads: not a session yet. `enrollment` appears once, for an operator who has no second factor. */
export const PlatformLinkResult = z.object({
  pendingToken: z.string(),
  expiresAt: IsoDateTime,
  step: z.enum(["enroll", "code"]),
  enrollment: z.object({ secret: z.string(), otpauthUrl: z.string() }).nullable(),
});
export type PlatformLinkResult = z.infer<typeof PlatformLinkResult>;

export const SecondFactorRequest = z.strictObject({ pendingToken: z.string().min(16).max(2048), code: z.string().trim().min(6).max(64) });
export type SecondFactorRequest = z.input<typeof SecondFactorRequest>;

/** The session after enrolling, with the recovery codes, which are never shown again. */
export const PlatformEnrolled = PlatformSession.extend({ recoveryCodes: z.array(z.string()) });
export type PlatformEnrolled = z.infer<typeof PlatformEnrolled>;

export const PLATFORM_AUDIT_ACTIONS = [
  "operator.added",
  "operator.updated",
  "operator.disabled",
  "operator.second_factor_enrolled",
  "operator.second_factor_reset",
  "operator.recovery_code_used",
  "host.created",
  "host.updated",
  "host.live_changed",
  "host.api_key_issued",
  "host.sso_key_added",
  "host.sso_key_retired",
  "host.sending_key_issued",
  "host.sending_key_revoked",
  "tenant.billing_mode_changed",
  "tenant.email_stop_lifted",
  "tenant.status_changed",
  "tenant.live_changed",
  "tenant.agency_changed",
  "support.console_write",
] as const;
export const PlatformAuditAction = z.enum(PLATFORM_AUDIT_ACTIONS);
export type PlatformAuditAction = z.infer<typeof PlatformAuditAction>;

export const PlatformAuditEntryDto = z.object({
  id: Uuid,
  at: IsoDateTime,
  /** `operator` signed in through the door; `cli` ran the operator command with the database's system connection. */
  actorKind: z.enum(["operator", "cli"]),
  actorId: Uuid.nullable(),
  actorName: z.string(),
  action: PlatformAuditAction,
  entity: z.enum(["operator", "host", "tenant", "support"]),
  entityId: z.string().nullable(),
  entityName: z.string().nullable(),
  before: z.unknown(),
  after: z.unknown(),
});
export type PlatformAuditEntryDto = z.infer<typeof PlatformAuditEntryDto>;
export const PlatformAuditPage = pageOf(PlatformAuditEntryDto);
export type PlatformAuditPage = z.infer<typeof PlatformAuditPage>;

export const AdminHostDto = z.object({
  id: Uuid,
  slug: z.string(),
  name: z.string(),
  billingMode: BillingMode,
  whiteLabel: WhiteLabel,
  tenants: z.object({ total: z.number().int(), active: z.number().int(), free: z.number().int(), stripe: z.number().int(), host: z.number().int() }),
  live: z.array(LiveSwitchState),
});
export type AdminHostDto = z.infer<typeof AdminHostDto>;
export const AdminHostList = z.object({ items: z.array(AdminHostDto) });

export const AdminHostPatch = z
  .strictObject({
    billingMode: BillingMode,
    whiteLabel: WhiteLabel,
  })
  .partial();
export type AdminHostPatch = z.infer<typeof AdminHostPatch>;

export const AdminTenantDto = z.object({
  id: Uuid,
  hostId: Uuid,
  hostSlug: z.string(),
  hostRef: z.string(),
  name: z.string(),
  country: z.string(),
  vertical: z.string(),
  status: TenantStatus,
  /** Its people and their roles come from the host, and so may its agency, on the host's next sign-in. */
  fedByHost: z.boolean(),
  agencyId: Uuid.nullable(),
  agencyName: z.string().nullable(),
  /** Its standing as an e-mail sender. Null until it has one: a new tenant with nothing against it. */
  emailStanding: z
    .object({
      state: z.enum(["ok", "warning", "stopped"]),
      reason: z.enum(["hard_bounces", "complaints"]).nullable(),
      stoppedAt: IsoDateTime.nullable(),
      sent: z.number().int(),
      hardBounces: z.number().int(),
      complaints: z.number().int(),
      warmupStep: z.number().int(),
      liftedAt: IsoDateTime.nullable(),
      liftedBy: z.string().nullable(),
      liftNote: z.string().nullable(),
    })
    .nullable(),
  billingMode: BillingMode,
  subscriptionStatus: SubscriptionStatus,
  /** Who put it in the current mode: an operator's name, or null when it came from the host's default. */
  billingModeSetBy: z.string().nullable(),
  billingModeSetAt: IsoDateTime.nullable(),
  billingModeReason: z.string().nullable(),
  hasStripeCustomer: z.boolean(),
  /** What the account accepted at signup. Null for one that came through a host, whose contract covers it. */
  termsVersion: z.string().nullable(),
  termsAcceptedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
});
export type AdminTenantDto = z.infer<typeof AdminTenantDto>;
export const AdminTenantPage = pageOf(AdminTenantDto);

export const AdminTenantQuery = z.object({
  hostId: Uuid.optional(),
  q: z.string().trim().max(120).optional(),
  billingMode: BillingMode.optional(),
  /** Only the tenants whose e-mail is stopped. */
  emailStopped: z.literal("true").optional(),
  cursor: Uuid.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

export const LiftEmailStopRequest = z.strictObject({ note: z.string().trim().min(3).max(300) });
export type LiftEmailStopRequest = z.infer<typeof LiftEmailStopRequest>;

export const SetBillingModeRequest = z.strictObject({ mode: BillingMode, reason: z.string().trim().min(3).max(300) });
export type SetBillingModeRequest = z.infer<typeof SetBillingModeRequest>;

export const SetTenantStatusRequest = z.strictObject({ status: TenantStatus, reason: z.string().trim().min(3).max(300) });
export type SetTenantStatusRequest = z.infer<typeof SetTenantStatusRequest>;

export const AdminLiveRequest = z.strictObject({ kind: z.enum(["messaging", "social", "ads"]), enabled: z.boolean() });
export type AdminLiveRequest = z.infer<typeof AdminLiveRequest>;
export const AdminLiveResponse = z.object({ switches: z.array(LiveSwitchState) });

export const MonthQuery = z.object({ month: Period.optional() });

export const AdminUsageRow = z.object({
  tenantId: Uuid,
  tenantName: z.string(),
  hostSlug: z.string(),
  billingMode: BillingMode,
  aiCostMicroUsd: z.number().int(),
  aiCalls: z.number().int(),
  smsSent: z.number().int(),
  emailsSent: z.number().int(),
});
export type AdminUsageRow = z.infer<typeof AdminUsageRow>;

/** Ledger rows of a closed month written after its snapshot, per account and meter. They were not billed and nothing bills them. */
export const LateUsageRow = z.object({
  tenantId: Uuid,
  tenantName: z.string(),
  hostSlug: z.string(),
  kind: UsageKind,
  rows: z.number().int(),
  amountMinor: z.number().int(),
  currency: z.string(),
  snapshotAt: IsoDateTime,
  lastWrittenAt: IsoDateTime,
});
export type LateUsageRow = z.infer<typeof LateUsageRow>;

export const LateUsageDto = z.object({ rows: z.number().int(), amountMinor: z.number().int(), currency: z.string(), items: z.array(LateUsageRow) });
export type LateUsageDto = z.infer<typeof LateUsageDto>;

/** Only totals: what a tenant did with them stays with the tenant. */
export const AdminUsageDto = z.object({
  month: Period,
  totals: z.object({ aiCostMicroUsd: z.number().int(), aiCalls: z.number().int(), smsSent: z.number().int(), emailsSent: z.number().int() }),
  byPayer: z.object({ service: z.number().int(), host: z.number().int(), tenant: z.number().int() }),
  rows: z.array(AdminUsageRow),
  usageNeedingReview: z.number().int(),
  unmatchedProviderEvents: z.number().int(),
  lateUsage: LateUsageDto,
});
export type AdminUsageDto = z.infer<typeof AdminUsageDto>;

export const ReconciliationLineDto = z.object({
  tenantId: Uuid,
  hostRef: z.string(),
  tenantName: z.string(),
  billingMode: BillingMode,
  status: TenantStatus,
  active: z.boolean(),
  billable: z.boolean(),
  aiCostMicroUsd: z.number().int(),
  smsSent: z.number().int(),
});
export type ReconciliationLineDto = z.infer<typeof ReconciliationLineDto>;

export const ReconciliationDto = z.object({
  hostId: Uuid,
  hostSlug: z.string(),
  period: Period,
  /** Closed periods never change; an open month is computed as of now. */
  closed: z.boolean(),
  closedAt: IsoDateTime.nullable(),
  activeTenants: z.number().int(),
  billableTenants: z.number().int(),
  freeTenants: z.number().int(),
  stripeTenants: z.number().int(),
  lines: z.array(ReconciliationLineDto),
});
export type ReconciliationDto = z.infer<typeof ReconciliationDto>;


export type DirectSession = z.infer<typeof DirectSession>;
export type AdminTenantQuery = z.infer<typeof AdminTenantQuery>;
export type MonthQuery = z.infer<typeof MonthQuery>;

export const AccountTenantDto = z.object({ id: Uuid, name: z.string(), status: z.string() });
export type AccountTenantDto = z.infer<typeof AccountTenantDto>;
export const AccountTenantList = z.object({ items: z.array(AccountTenantDto) });
export type AccountTenantList = z.infer<typeof AccountTenantList>;

export const PublicBrandQuery = z.object({ domain: z.string().trim().max(253).optional() });
export const HostActiveTenantsDto = z.object({ total: z.number().int(), active: z.number().int(), free: z.number().int(), stripe: z.number().int(), host: z.number().int() });
export type HostActiveTenantsDto = z.infer<typeof HostActiveTenantsDto>;
